import i18n from '@/utils/i18n';
import MousePointerClickIcon from '@assets/icons/mouse-pointer-click.svg';
import PencilThinIcon from '@assets/icons/pencil-thin.svg';
import React from 'react';
import UndoIcon from '@assets/icons/undo.svg';
import { createStyles } from '@/contexts/theme-context';
import { Icons } from '@usepepper/sprout-kit-ui';
import { isWeb } from '@/utils/navigation';
import { Modal } from '@sales-portal/components/modal';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const MODAL_WIDTH = 520;
const ROW_ICON_SIZE = 18;

interface Props {
  defaultAccepted?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const PricingAgentHelpModal: React.FC<Props> = ({
  defaultAccepted = false,
  isOpen,
  onClose,
}) => {
  const [styles, theme] = useStyles();

  const onceLabel = i18n.t(
    `pricing.agent.review.help_modal.${isWeb ? 'click_once' : 'tap_once'}`,
  );
  const twiceLabel = i18n.t(
    `pricing.agent.review.help_modal.${isWeb ? 'click_twice' : 'tap_twice'}`,
  );

  const clickIcon = isWeb ? (
    <MousePointerClickIcon
      height={ROW_ICON_SIZE}
      stroke={theme.sk.color.icon.secondary}
      width={ROW_ICON_SIZE}
    />
  ) : (
    <Icons.Pointer color={theme.sk.color.icon.secondary} size={ROW_ICON_SIZE} />
  );
  const undoIcon = (
    <UndoIcon
      height={ROW_ICON_SIZE}
      stroke={theme.sk.color.icon.secondary}
      width={ROW_ICON_SIZE}
    />
  );

  const clickRows = defaultAccepted
    ? [
        {
          action: i18n.t('pricing.agent.review.status.rejected'),
          backgroundColor: theme.sk.color.bg.error,
          icon: clickIcon,
          label: onceLabel,
          textColor: theme.sk.color.text.error,
        },
        {
          action: i18n.t('pricing.agent.review.status.accepted'),
          backgroundColor: theme.sk.color.bg.success,
          icon: undoIcon,
          label: i18n.t('pricing.agent.review.help_modal.click_again'),
          textColor: theme.sk.color.text.success,
        },
      ]
    : [
        {
          action: i18n.t('pricing.agent.review.status.accepted'),
          backgroundColor: theme.sk.color.bg.success,
          icon: clickIcon,
          label: onceLabel,
          textColor: theme.sk.color.text.success,
        },
        {
          action: i18n.t('pricing.agent.review.status.rejected'),
          backgroundColor: theme.sk.color.bg.error,
          icon: clickIcon,
          label: twiceLabel,
          textColor: theme.sk.color.text.error,
        },
        {
          action: i18n.t('reset'),
          backgroundColor: theme.sk.color.bg.primary.default,
          icon: undoIcon,
          label: i18n.t('pricing.agent.review.help_modal.click_again'),
          textColor: theme.sk.color.text.primary,
        },
      ];

  const helpModalRows = [
    ...clickRows,
    {
      action: i18n.t('pricing.agent.review.status.overridden'),
      backgroundColor: theme.sk.color.bg.blue,
      icon: (
        <PencilThinIcon
          height={ROW_ICON_SIZE}
          stroke={theme.sk.color.icon.secondary}
          width={ROW_ICON_SIZE}
        />
      ),
      label: i18n.t('pricing.agent.review.help_modal.edit_values'),
      textColor: theme.sk.color.text.blue,
    },
  ];

  return (
    <Modal
      headerElement={
        <View style={styles.header}>
          <Typography color={theme.sk.color.text.primary} variant="heading-md">
            {i18n.t('pricing.agent.review.help_modal.title')}
          </Typography>
          <Typography
            color={theme.sk.color.text.secondary}
            variant="paragraph-md"
          >
            {i18n.t('pricing.agent.review.help_modal.subtitle')}
          </Typography>
        </View>
      }
      isOpen={isOpen}
      onClose={onClose}
      width={MODAL_WIDTH}
    >
      <View style={styles.rowsContainer}>
        {helpModalRows.map(
          ({ action, backgroundColor, textColor, icon, label }, index) => (
            <View
              key={label}
              style={[
                styles.row,
                index < helpModalRows.length - 1 && styles.rowDivider,
              ]}
              testID={`help-modal-row-${label}`}
            >
              <View style={styles.rowLabelCell}>
                {icon}
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-md"
                >
                  {label}
                </Typography>
              </View>
              <View
                style={[
                  styles.rowActionCell,
                  backgroundColor !== undefined && {
                    backgroundColor: backgroundColor,
                  },
                ]}
              >
                <Typography color={textColor} variant="paragraph-md-600">
                  {action}
                </Typography>
              </View>
            </View>
          ),
        )}
      </View>
      <View style={styles.footer}>
        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-md"
        >
          {i18n.t(
            defaultAccepted
              ? 'pricing.agent.review.help_modal.footer_auto_accepted'
              : 'pricing.agent.review.help_modal.footer',
          )}
        </Typography>
      </View>
    </Modal>
  );
};

const useStyles = createStyles((theme) => ({
  footer: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
  header: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  row: {
    alignItems: 'stretch',
    flexDirection: 'row',
    minHeight: 40,
  },
  rowActionCell: {
    alignItems: 'flex-start',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowDivider: {
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 1,
  },
  rowLabelCell: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowsContainer: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    overflow: 'hidden',
  },
}));
